import React, { useState, useEffect, useMemo } from 'react';
import ContentWrapper from '../Layout/ContentWrapper';
import {
  Card,
  CardHeader,
  CardFooter,
  CardBody,
  Input,
  ButtonGroup,
  Button,
  DropdownToggle,
  Dropdown,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import ApplicationsTable from './Components/Tables/applicationsTable';
import Kanban from './Components/KanBans/Kanban';
import Select from 'react-select';
import Pagination from './Components/shared/pagination';
import debounce from 'lodash/debounce';
import * as queryString from 'query-string';
import * as utils from '../Application/Services/utils';
import { getApplications } from './Services/applicationService';
import { displayPaginationMessage } from './Helpers/jobHelper';
import GooglePlacesAutocomplete, { geocodeByPlaceId, getLatLng } from 'react-google-places-autocomplete';
import { SpinnerLoader } from './Components/shared/spinnerLoader';
import { updateCompanyKanbanSettings } from './Services/kanbanService';
import { KanbanUpdateType } from './Helpers/enums';
import { useTranslation } from 'react-i18next';
import useCompanyStore from '../../stores/companyStore';
import useConfigStore from '../../stores/configStore';

const JOB_TYPE = [
  { value: 'All', label: 'Job Type...' },
  { value: 'Open', label: 'Open' },
  { value: 'Bounded', label: 'Bounded' },
];

const pageSizeFromLocalStore =
  localStorage.getItem('numItemsPerPageApplications') !== null
    ? localStorage.getItem('numItemsPerPageApplications')
    : 10;

let ApplicationParams = {
  JobTitle: '',
  FirstName: '',
  LastName: '',
  JobType: JOB_TYPE[0],
  PageNumber: 1,
  PageSize: pageSizeFromLocalStore,
  ApplicationType: null,
  JobId: 0,
  ApplicationStatus: null,
  OnlyNew: false,
  SortByScore: 0,
  MineOrAllSelected: '1',
  City: '',
  DistanceFromCity: utils.getDistance()[0],
  Lat: 0,
  Lng: 0,
};

const Applications = ({ location }) => {
  const { t } = useTranslation();
  const [totalItems, setTotalItems] = useState(0);
  const [selectTypeValue, setSelectTypeValue] = useState('');
  const [selectStatusValue, setSelectStatusValue] = useState('');
  const [applications, setApplications] = useState([]);
  const [applicationParams, setApplicationParams] = useState(ApplicationParams);
  //const [showHelpModal, setShowHelpModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [applicationView, setApplicationView] = useState(
    localStorage.getItem('applicationView') !== null ? Number(localStorage.getItem('applicationView')) : 1
  );
  const [applicationRecruitingCheckBoxValue, setApplicationRecruitingCheckBoxValue] = useState(
    localStorage.getItem('ApplicationRecruitingCheckBoxValue') === 'true' ||
      localStorage.getItem('ApplicationRecruitingCheckBoxValue') === null
      ? true
      : false
  );
  const [applicationOnboardingCheckBoxValue, setApplicationOnboardingCheckBoxValue] = useState(
    localStorage.getItem('ApplicationOnboardingCheckBoxValue') === 'true' ||
      localStorage.getItem('ApplicationOnboardingCheckBoxValue') === null
      ? true
      : false
  );
  // const [triggerShowAll, setTriggerShowAll] = useState(undefined);
  const [isDropDownOpen, setIsDropDownOpen] = useState(false);
  const [pagination, setPagination] = useState({});

  const company = useCompanyStore((state) => state.company);
  const fetchCompany = useCompanyStore((state) => state.fetchCompany);
  const gApi = useConfigStore((state) => state.gApi);

  const [parsedKanbanSettings, setParsedKanbanSettings] = useState();

  const ClearParams = () => ({
    ...applicationParams,
    ApplicationType: null,
    PageNumber: 1,
    FirstName: '',
    LastName: '',
    JobType: JOB_TYPE[0],
    ApplicationStatus: null,
    JobTitle: '',
    OnlyNew: false,
    SortByScore: 0,
  });

  const applicationList = () => {
    return parsedKanbanSettings?.map((item) => ({
      value: item.Id,
      label: item.Title,
    }));
  };

  useEffect(() => {
    if (company?.kanbanSettings) {
      try {
        // Ensure we have an array
        const settings =
          typeof company.kanbanSettings === 'string' ? JSON.parse(company.kanbanSettings) : company.kanbanSettings;

        setParsedKanbanSettings(settings);
      } catch (error) {
        console.error('Error parsing kanban settings:', error);
        setParsedKanbanSettings([]);
      }
    }
  }, [company]);

  // Run on mount
  useEffect(() => {
    //handlePrepareKanbanSettings();
    refreshData();

    return () => {
      debouncedSearchByName.cancel();
      debouncedSearchBySecondName.cancel();
      debouncedSearchJobTitle.cancel();
    };
  }, []);

  const toggleDropDown = () => {
    setIsDropDownOpen((prev) => !prev);
  };

  const refreshData = async () => {
    setLoading(true);
    try {
      // 1. Ensure we have a valid page size in localStorage
      let storedPageSize = localStorage.getItem('numItemsPerPageApplications');
      if (!storedPageSize) {
        storedPageSize = '10';
        localStorage.setItem('numItemsPerPageApplications', storedPageSize);
      }
      const pageSize = Number(storedPageSize);

      // 2. Parse any URL params
      const parsedQuery = queryString.parse(location.search);
      const newParams = {
        ...applicationParams,
        PageSize: pageSize,
        JobTitle: parsedQuery.jobTitle || '',
        FirstName: parsedQuery.applicantName || '',
        LastName: parsedQuery.applicantLastName || '',
        JobId: parsedQuery.jobId ? Number(parsedQuery.jobId) : 0,
        OnlyNew: Boolean(parsedQuery.onlyNew),
        MineOrAllSelected: '1',
      };

      // 3. Fetch from the backend
      const response = await getApplications(newParams);

      // 4. Basic validation
      if (!response || !response.data || !response.headers) {
        console.error('Invalid response structure:', response);
        return;
      }
      const { data: apps, headers } = response;

      // 5. Parse pagination header, with safe fallback
      let pag = {
        totalItems: 0,
        pageSize,
        currentPage: newParams.PageNumber,
        totalPages: 1,
      };
      if (headers.pagination) {
        try {
          pag = JSON.parse(headers.pagination);
        } catch (err) {
          console.error('Error parsing pagination header:', err);
        }
      }

      // 6. Update state
      setApplications(apps);
      setApplicationParams(newParams);
      setTotalItems(pag.totalItems ?? 0);
      setPagination(pag);
      setSelectStatusValue(null);
      setSelectTypeValue(null);
    } catch (error) {
      console.error('Failed to refresh applications:', error);
      // you could also show a toast here, e.g. toast.error('Could not load applications');
    } finally {
      setLoading(false);
    }
  };

  const searchByName = (text) => {
    const updatedParams = { ...applicationParams, FirstName: text };
    setApplicationParams(updatedParams);
    debouncedSearchByName(updatedParams);
  };

  const searchBySecondName = (text) => {
    const updatedParams = { ...applicationParams, LastName: text };
    setApplicationParams(updatedParams);
    debouncedSearchBySecondName(updatedParams);
  };

  const searchByJobTitle = (text) => {
    const updatedParams = { ...applicationParams, JobTitle: text };
    setApplicationParams(updatedParams);
    debouncedSearchJobTitle(updatedParams);
  };

  const handleCitySelect = (address) => {
    const placeId = address.value.place_id;
    const mainName = address.value.structured_formatting.main_text;
    const cityTmp = { label: mainName, value: mainName };

    geocodeByPlaceId(placeId)
      .then((results) => getLatLng(results[0]))
      .then(({ lat, lng }) => {
        const newParams = { ...applicationParams, City: cityTmp, Lat: lat, Lng: lng, PageNumber: 1 };
        setApplicationParams(newParams);
        getApplications(newParams).then((result) => {
          setApplications(result.data);
          setPagination(JSON.parse(result.headers.pagination));
        });
      })
      .catch((error) => console.error(error));
  };

  const onDistanceChanged = (item) => {
    const newParams = { ...applicationParams, DistanceFromCity: item };
    setApplicationParams(newParams);
    getApplications(newParams).then((result) => {
      setApplications(result.data);
      setPagination(JSON.parse(result.headers.pagination));
    });
  };

  const debouncedSearchByName = useMemo(
    () =>
      debounce(async (updatedParams) => {
        updatedParams.PageNumber = 1;
        const { data: apps, headers } = await getApplications(updatedParams);
        const pag = JSON.parse(headers.pagination);
        setApplications(apps);
        setTotalItems(pag.totalItems);
      }, 1000),
    []
  );

  const debouncedSearchBySecondName = useMemo(
    () =>
      debounce(async (updatedParams) => {
        updatedParams.PageNumber = 1;
        const { data: apps, headers } = await getApplications(updatedParams);
        const pag = JSON.parse(headers.pagination);
        setApplications(apps);
        setTotalItems(pag.totalItems);
        setPagination(pag);
      }, 1000),
    []
  );

  const debouncedSearchJobTitle = useMemo(
    () =>
      debounce(async (updatedParams) => {
        updatedParams.PageNumber = 1;
        const { data: apps, headers } = await getApplications(updatedParams);
        const pag = JSON.parse(headers.pagination);
        setApplications(apps);
        setTotalItems(pag.totalItems);
        setPagination(pag);
      }, 1000),
    []
  );

  const onSelectApplicationTypeChanged = async (item) => {
    setLoading(true);
    const updatedParams = { ...applicationParams, PageNumber: 1, ApplicationType: item };
    const { data: apps, headers } = await getApplications(updatedParams);
    const pag = JSON.parse(headers.pagination);
    setApplications(apps);
    setApplicationParams(updatedParams);
    setSelectTypeValue(item);
    setTotalItems(pag.totalItems);
    setPagination(pag);
    setLoading(false);
  };

  const onSelectApplicationStatusChanged = async (item) => {
    setLoading(true);
    const updatedParams = { ...applicationParams, ApplicationStatus: item, PageNumber: 1 };
    const { data: apps, headers } = await getApplications(updatedParams);
    const pag = JSON.parse(headers.pagination);
    setApplications(apps);
    setApplicationParams(updatedParams);
    setSelectStatusValue(item);
    setTotalItems(pag.totalItems);
    setPagination(pag);
    setLoading(false);
  };

  const handleChangeSelect = async (name, newVal) => {
    setLoading(true);
    const newParams = { ...applicationParams, PageSize: newVal.value, PageNumber: 1 };
    const { data: apps, headers } = await getApplications(newParams);
    const pag = JSON.parse(headers.pagination);
    setApplicationParams(newParams);
    setApplications(apps);
    setPagination(pag);
    localStorage.setItem('numItemsPerPageApplications', newVal.value);
    setLoading(false);
  };

  // Updated ResetParams to use the cleared parameters directly
  const ResetParams = async () => {
    setLoading(true);

    // Get the new cleared parameters
    const newParams = ClearParams();
    setApplicationParams(newParams);

    let numItemsPerPageCompetences = localStorage.getItem('numItemsPerPageApplications');
    if (numItemsPerPageCompetences === null) {
      localStorage.setItem('numItemsPerPageApplications', 10);
      numItemsPerPageCompetences = 10;
    }

    const { data: apps, headers } = await getApplications(newParams);
    const pag = JSON.parse(headers.pagination);
    setApplications(apps);
    setSelectTypeValue(null);
    setSelectStatusValue(null);
    setTotalItems(pag.totalItems);
    setPagination(pag);
    setLoading(false);
  };

  const handlePaginationSelected = async (selectedPageX) => {
    setLoading(true);
    const updatedParams = { ...applicationParams, PageNumber: selectedPageX };
    const { data: apps, headers } = await getApplications(updatedParams);
    const pag = JSON.parse(headers.pagination);
    setApplicationParams(updatedParams);
    setApplications(apps);
    setLoading(false);
    setPagination(pag);
  };

  const onRadioBtnClick = (view) => {
    setApplicationView(view);
  };

  const onApplicationRecruitingCheckBoxChange = (event) => {
    const checked = event.currentTarget.checked;

    const updatedSettings = parsedKanbanSettings.map((column) => {
      if (column.Tag === 'recruiting') {
        return { ...column, Visible: checked };
      }
      return column;
    });

    updateKanbanSettingsOnBackend(updatedSettings);
    setApplicationRecruitingCheckBoxValue(checked);
  };

  const updateKanbanSettingsOnBackend = (updatedSettings) => {
    const stringifiedSettings = JSON.stringify(updatedSettings);
    setParsedKanbanSettings(updatedSettings);
    updateCompanyKanbanSettings(KanbanUpdateType.Visibility, company.id, stringifiedSettings)
      .then(() => {
        fetchCompany();
      })
      .catch((error) => {
        //console.error('Error updating kanban settings:', error);
      });
  };

  const onApplicationOnboardingCheckBoxChange = (event) => {
    const checked = event.currentTarget.checked;

    const updatedSettings = parsedKanbanSettings.map((column) => {
      if (column.Tag === 'onboarding') {
        return { ...column, Visible: checked };
      }
      return column;
    });

    updateKanbanSettingsOnBackend(updatedSettings);
    setApplicationOnboardingCheckBoxValue(checked);
  };

  const areAllColumnTypesChecked = (columns, tag) => {
    // check if columns are not null
    if (!columns) return false;

    const typeColumns = columns.filter((col) => col.Tag === tag);
    return typeColumns?.length > 0 && typeColumns.every((col) => col.Visible);
  };

  // Modify the handleColumnVisibilityChange function
  const handleColumnVisibilityChange = (columnId) => {
    const updatedSettings = parsedKanbanSettings.map((column) => {
      if (column.Id === columnId) {
        return { ...column, Visible: !column.Visible };
      }
      return column;
    });

    updateKanbanSettingsOnBackend(updatedSettings);

    // Check parent checkboxes state
    const areAllRecruitingChecked = areAllColumnTypesChecked(updatedSettings, 'recruiting');
    const areAllOnboardingChecked = areAllColumnTypesChecked(updatedSettings, 'onboarding');

    setApplicationRecruitingCheckBoxValue(areAllRecruitingChecked);
    setApplicationOnboardingCheckBoxValue(areAllOnboardingChecked);
  };

  // Update the useEffect to set initial parent checkbox states
  useEffect(() => {
    const areAllRecruitingChecked = areAllColumnTypesChecked(parsedKanbanSettings, 'recruiting');
    const areAllOnboardingChecked = areAllColumnTypesChecked(parsedKanbanSettings, 'onboarding');

    setApplicationRecruitingCheckBoxValue(areAllRecruitingChecked);
    setApplicationOnboardingCheckBoxValue(areAllOnboardingChecked);
  }, [parsedKanbanSettings]);

  const renderColumnCheckboxes = (columns, tag, t) => {
    if (!columns) return null; // Check if columns are not null

    return columns
      .filter((col) => col.Tag === tag)
      .map((column) => (
        <div key={column.Id} className="checkbox c-checkbox px-5">
          <label>
            <input type="checkbox" checked={column.Visible} onChange={() => handleColumnVisibilityChange(column.Id)} />
            <span className="fa fa-check"></span> {column.Title}
          </label>
        </div>
      ));
  };

  return (
    <ContentWrapper>
      <div className="content-heading">
        <div>
          {/* <Trans i18nKey="applications.APPLICATIONS" /> */}
          {t('applications.APPLICATIONS')}
          {applicationParams.JobId !== 0 ? (
            <>
              {' for '}
              <strong>{applications[0]?.jobTitle || ''}</strong>
            </>
          ) : null}
          &nbsp;
          {/* <HelpModal purpose="applicationsHelp" showModal={showHelpModal} onToggle={toggleHelpModal} /> */}
          <small>
            {/* <Trans i18nKey="dashboard.WELCOME_BETA" /> */}
            {t('dashboard.WELCOME_BETA')}
          </small>
        </div>
      </div>
      {loading ? (
        <SpinnerLoader />
      ) : (
        <Card className="card-default">
          <CardHeader>
            <div className="d-flex justify-content-between align-items-start">
              <div className="form-row align-items-center">
                <div style={{ width: '200px' }}>
                  <GooglePlacesAutocomplete
                    apiKey={gApi}
                    selectProps={{
                      value: applicationParams.City,
                      styles: {
                        input: (provided) => ({ ...provided }),
                        option: (provided) => ({ ...provided }),
                        singleValue: (provided) => ({ ...provided }),
                      },
                      onChange: (item) => handleCitySelect(item),
                      placeholder: t('applicants.SEARCH_BY_LOCATION'),
                    }}
                  />
                </div>
                <div style={{ width: '100px' }}>
                  <Select
                    name="applicantDistanceFromCitySelect"
                    value={applicationParams.DistanceFromCity}
                    onChange={(item) => onDistanceChanged(item)}
                    options={utils.getDistance()}
                    placeholder="Km"
                    isDisabled={applicationParams.City === ''}
                  />
                </div>
                <div style={{ width: '150px' }}>
                  <Input
                    type="text"
                    value={applicationParams.JobTitle}
                    onChange={(e) => searchByJobTitle(e.target.value)}
                    id="inlineFormInput"
                    placeholder={t('applications.SEARCH_BY_JOB_TITLE')}
                  />
                </div>
                <div className="col-auto">
                  <Input
                    type="text"
                    value={applicationParams.FirstName}
                    onChange={(e) => searchByName(e.target.value)}
                    id="inputFirstName"
                    placeholder={t('applicants.SEARCH_BY_NAME')}
                  />
                </div>
                <div className="col-auto">
                  <Input
                    type="text"
                    value={applicationParams.LastName}
                    onChange={(e) => searchBySecondName(e.target.value)}
                    id="inputSecondName"
                    placeholder={t('applications.SEARCH_BY_APPLICANT_LAST_NAME')}
                  />
                </div>
                <div style={{ width: '150px' }}>
                  <Select
                    name="tags"
                    value={selectStatusValue}
                    onChange={(item) => onSelectApplicationStatusChanged(item)}
                    options={applicationList()}
                    isClearable={true}
                    placeholder={t('applications.SELECT_STATUS')}
                  />
                </div>
                &nbsp;
                <div style={{ width: '150px' }}>
                  <Select
                    name="tags"
                    value={selectTypeValue}
                    onChange={(item) => onSelectApplicationTypeChanged(item)}
                    options={utils.getApplicationType()}
                    isClearable={true}
                    placeholder={t('applications.SELECT_TYPE')}
                  />
                </div>
                <div className="col">
                  <button className="btn btn-primary" onClick={ResetParams}>
                    {/* <Trans i18nKey="common.CLEAR_FILTERS" /> */}
                    {t('common.CLEAR_FILTERS')}
                  </button>
                </div>
              </div>
              <div>
                <Dropdown isOpen={isDropDownOpen} toggle={toggleDropDown}>
                  <DropdownToggle
                    color="link"
                    className="d-flex justify-content-end w-100"
                    style={{ textDecoration: 'none' }}
                  >
                    <em className="fa-2x icon-settings text-muted far icon-hover-effect-no-color"></em>
                  </DropdownToggle>
                  <DropdownMenu className="dropdown-menu-right-forced animated fadeInLeft">
                    <DropdownItem>
                      <div className="d-flex justify-content-end">
                        <ButtonGroup outline>
                          <Button
                            color="primary"
                            outline
                            onClick={() => onRadioBtnClick(1)}
                            active={applicationView === 1}
                          >
                            {/* <Trans i18nKey="kanban.KANBAN_VIEW" /> */}
                            {t('kanban.KANBAN_VIEW')}
                          </Button>
                          <Button
                            color="primary"
                            outline
                            onClick={() => onRadioBtnClick(0)}
                            active={applicationView === 0}
                          >
                            {/* <Trans i18nKey="kanban.TABLE_VIEW" /> */}
                            {t('kanban.TABLE_VIEW')}
                          </Button>
                        </ButtonGroup>
                      </div>
                    </DropdownItem>
                    <DropdownItem divider />
                    {/* Recruiting section */}
                    <div className="checkbox c-checkbox px-4 pt-2">
                      <h4>
                        <label>
                          <input
                            id="checkboxRecruitingColumns"
                            type="checkbox"
                            checked={applicationRecruitingCheckBoxValue}
                            onChange={onApplicationRecruitingCheckBoxChange}
                          />
                          <span className="fa fa-check"></span> {t('kanban.RECRUITING')}
                        </label>
                      </h4>
                    </div>
                    {renderColumnCheckboxes(parsedKanbanSettings, 'recruiting', t)}

                    <DropdownItem divider />
                    {/* Onboarding section */}
                    <div className="checkbox c-checkbox px-4 pt-2">
                      <h4>
                        <label>
                          <input
                            id="checkboxOnboardingColumns"
                            type="checkbox"
                            checked={applicationOnboardingCheckBoxValue}
                            onChange={onApplicationOnboardingCheckBoxChange}
                          />
                          <span className="fa fa-check"></span> {t('kanban.ONBOARDING')}
                        </label>
                      </h4>
                    </div>
                    {renderColumnCheckboxes(parsedKanbanSettings, 'onboarding', t)}
                  </DropdownMenu>
                </Dropdown>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            {applicationView === 0 ? (
              <ApplicationsTable
                applications={applications}
                counting={(applicationParams.PageNumber - 1) * applicationParams.PageSize}
                refreshStatus={refreshData}
              />
            ) : (
              <>
                {/* <Pagination
                  totalItems={totalItems}
                  pageSize={applicationParams.PageSize}
                  activePage={applicationParams.PageNumber}
                  onPaginationSelect={(item) => handlePaginationSelected(item)}
                  onTableSizeSelect={(item) => handleChangeSelect('tags', item)}
                  pagination={pagination}
                /> */}
                {displayPaginationMessage(pagination, t)}
                <Kanban
                  applications={applications}
                  refreshStatus={refreshData}
                  parsedKanbanSettings={parsedKanbanSettings?.filter((column) => column.Visible)}
                  jobId={applicationParams.JobId}
                />
              </>
            )}
          </CardBody>
          <CardFooter>
            <Pagination
              totalItems={totalItems}
              pageSize={applicationParams.PageSize}
              activePage={applicationParams.PageNumber}
              onPaginationSelect={(item) => handlePaginationSelected(item)}
              onTableSizeSelect={(item) => handleChangeSelect('tags', item)}
              pagination={pagination}
            />
          </CardFooter>
        </Card>
      )}
    </ContentWrapper>
  );
};

export default Applications;
